/**
 * MasterReadOnlySection + MemberCard + Field — section haute de ImmeubleDossierDetail
 * (bandeau Master en lecture seule synchronisé via GET /masters/{id}).
 *
 * Extrait de ImmeubleDossierDetail.jsx (Batch #14.221).
 */
import { FormField as Field } from "@/components/shared/FormField";
import { Link } from "react-router-dom";
import { User, Mail, Phone, MapPin, Lock, ExternalLink } from "lucide-react";

//  Section Master en lecture seule (identique au Fiscal)
// ============================================================
function MasterReadOnlySection({ master, masterId }) {
  if (!master) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm text-amber-800">
        Master {masterId.slice(0, 8)}… introuvable (peut-être archivé). Le dossier immeuble reste consultable.
      </div>
    );
  }

  const cc = master.common_contact || {};
  const fullAddr = [
    [cc.civic_number, cc.street].filter(Boolean).join(" "),
    cc.apartment ? `App. ${cc.apartment}` : null,
    cc.city, cc.province, cc.postal_code,
  ].filter(Boolean).join(", ");

  return (
    <div className="border border-slate-200 bg-slate-50/60 rounded-xl p-5" data-testid="immeuble-master-readonly">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-500" /> Informations Master (synchronisées, lecture seule)
        </h2>
        <Link
          to={`/admin/masters/${master.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1"
          data-testid="immeuble-master-edit-link"
        >
          Modifier la fiche Master <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <MemberCard user={master.primary_user} role="Principal" tone="indigo" />
        {master.secondary_user ? (
          <MemberCard user={master.secondary_user} role="Secondaire" tone="purple" />
        ) : (
          <div className="border border-dashed border-slate-300 rounded-lg p-4 text-center text-xs text-slate-400">
            Aucun conjoint·e lié
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 pt-3 text-sm">
        {fullAddr ? (
          <div className="inline-flex items-start gap-2 text-slate-700">
            <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <span data-testid="immeuble-master-address">{fullAddr}</span>
          </div>
        ) : (
          <div className="text-slate-400 italic text-xs">Aucune adresse renseignée dans le Master.</div>
        )}
        {cc.home_phone && (
          <div className="inline-flex items-center gap-2 text-slate-600 ml-4">
            <Phone className="w-4 h-4 text-slate-400" /> {cc.home_phone} (maison)
          </div>
        )}
      </div>
    </div>
  );
}

function MemberCard({ user, role, tone }) {
  if (!user) return null;
  const tones = {
    indigo: "bg-indigo-100 text-indigo-700",
    purple: "bg-purple-100 text-purple-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`immeuble-member-${role.toLowerCase()}`}>
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600 flex-shrink-0">
          {(user.first_name?.[0] || "").toUpperCase()}{(user.last_name?.[0] || "").toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800 text-sm truncate">{user.first_name} {user.last_name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tones[tone] || tones.indigo}`}>{role}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 space-y-0.5">
            {user.date_naissance && <div className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {user.date_naissance}</div>}
            {user.email && <div className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {user.email}</div>}
            {user.phone && <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {user.phone}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  Section PROPRIÉTÉS — accordéons éditables CRUD complet
//  Property (niveau 1) → Apartment (niveau 2) → Tenant (flat)
//  Étape 3 : ajout/édition/suppression via 3 modales unifiées

export { MasterReadOnlySection, MemberCard, Field };
